import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { submitContactForm } from '../../../redux/contactUs/contactUsActions';
import { fetchUserData, clearUserData } from '../../../redux/agent/agentActions';
import { sendAgentMessage, loadMoreSearchSpaces, MAX_CARDS_PER_SEARCH } from '../utils/agentApiUtils';
import { replyTextForMessage } from '../utils/agentReplyUtils';
import { resolveSpaceImagePublicId } from '../utils/cloudinaryImageUtils';
import { resolveSpaceCardBookingUrl, toRelativeBookingPath } from '../utils/bookingUrlUtils';
import { saveChatSession, loadChatSession, clearChatSession } from '../utils/sessionPersistence';
import { buildChipRemovalMessage } from '../utils/chipRemovalMessages';
import { shouldHideChatbot } from '../utils/urlUtils';
import { buildAgentSearchResultsUrl } from '../../../helper/searchResultsUrl';
import { IMAGES_BASE_URL } from '../../../settings';
import {
  getInitialContactFormState,
  validateContactForm,
  sanitizeFormData,
  formatFormDataForSubmission,
  resetForm,
  isFormReadyToSubmit,
} from '../utils/formUtils';
import { handleFormFieldChange } from '../utils/formUtils';
import {
  CHATBOT_CONFIG,
  CONTACT_FORM_MESSAGES,
  DISCLAIMER_MESSAGES,
  FORM_LABELS,
  FORM_PLACEHOLDERS,
  FORM_BUTTONS,
  FORM_FIELD_NAMES,
  INPUT_TYPES,
  INFO_PANEL_MESSAGES,
  CHAT_COMMANDS,
  HELP_INFORMATION,
  LOADING_MESSAGES,
} from '../constants';
import '../../../assets/scss/components/Agent/ChatbotPopup.scss';

// ─── Simple markdown renderer ─────────────────────────────────────────────────
// Handles **bold**, [label](url) links, and newlines returned by the LLM
const MD_LINK_PATTERN = /(\[[^\]]+\]\([^)]+\))/g;
const MD_LINK_PARSE = /^\[([^\]]+)\]\(([^)]+)\)$/;

const renderInlinePart = (part, key) => {
  if (!part) return null;

  const linkMatch = part.match(MD_LINK_PARSE);
  if (linkMatch) {
    const [, label, rawUrl] = linkMatch;
    const href = toRelativeBookingPath(rawUrl) || rawUrl;
    return (
      <a
        key={key}
        href={href}
        className="chatbot-inline-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }

  return part.split(/(\*\*[^*]+\*\*)/g).map((segment, j) => {
    if (segment.startsWith('**') && segment.endsWith('**')) {
      return <strong key={`${key}-b-${j}`}>{segment.slice(2, -2)}</strong>;
    }
    return segment || null;
  });
};

const renderText = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, i) => (
    <span key={i}>
      {line.split(MD_LINK_PATTERN).map((part, j) => renderInlinePart(part, `${i}-${j}`))}
      {i < lines.length - 1 && <br />}
    </span>
  ));
};

// ─── Space card helpers ───────────────────────────────────────────────────────

const COUNTRY_URL_MAP = {
  LK: 'Sri-Lanka', SG: 'Singapore', MY: 'Malaysia',
  AU: 'Australia', IN: 'India', GB: 'United-Kingdom', US: 'United-States',
};

const buildBookingUrl = (
  spaceId,
  countryCode,
  unit = 'hour',
  startDate,
  endDate,
  eventTypeId = '',
  startTime,
  endTime,
  peopleCount,
) => {
  const country = COUNTRY_URL_MAP[countryCode] || 'Sri-Lanka';
  const dateParams = startDate
    ? `&urlStartDate=${startDate}&urlEndDate=${endDate || startDate}`
    : '';
  const timeParams = startTime && endTime
    ? `&urlStartTime=${encodeURIComponent(startTime)}&urlEndTime=${encodeURIComponent(endTime)}`
    : '';
  const guestParam = peopleCount && Number(peopleCount) > 1 ? `&urlPeopleCount=${peopleCount}` : '';
  const eventParam = eventTypeId ? `&eventTypeId=${eventTypeId}` : '';
  return `/${country}/space/bookings?spaceId=${spaceId}${eventParam}${dateParams}${timeParams}&unit=${unit}${guestParam}`;
};

const buildImageUrl = (publicId) => {
  if (!publicId) return null;
  return `${IMAGES_BASE_URL}image/upload/w_600,h_360,c_fill,q_auto,f_auto/${publicId}`;
};

// ─── Component ────────────────────────────────────────────────────────────────

const ChatbotPopupV2 = ({ isOpen, onClose }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [agentHistory, setAgentHistory] = useState([]);  // LLM conversation history
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [contactForm, setContactForm] = useState(getInitialContactFormState());
  const [contactFormErrors, setContactFormErrors] = useState({});
  const [initialized, setInitialized] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState('');
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [loadingMoreMessageId, setLoadingMoreMessageId] = useState(null);

  const messagesEndRef = useRef(null);
  const dispatch = useDispatch();

  // ─── Offline detection ───────────────────────────────────────────────────
  useEffect(() => {
    const setOnline  = () => setIsOffline(false);
    const setOffline = () => setIsOffline(true);
    window.addEventListener('online',  setOnline);
    window.addEventListener('offline', setOffline);
    return () => {
      window.removeEventListener('online',  setOnline);
      window.removeEventListener('offline', setOffline);
    };
  }, []);

  // ─── Auth & user data ────────────────────────────────────────────────────
  const authDetails = useSelector((state) => state?.auth?.loginDetails?.data?.token || null);
  const isLoggedIn = !!authDetails;
  const wasLoggedInRef = useRef(isLoggedIn);
  const userData = useSelector((state) => state?.agent?.userData || null);
  const userDataLoading = useSelector((state) => state?.agent?.userDataLoading || false);

  const userContext = {
    isLoggedIn,
    userName: userData?.userName || '',
    bookingCount: userData?.bookingCount || 0,
  };

  // ─── Fetch user data on login ────────────────────────────────────────────
  useEffect(() => {
    if (isLoggedIn && !userData && !userDataLoading) {
      dispatch(fetchUserData());
    }
    if (!isLoggedIn) {
      dispatch(clearUserData());
    }
  }, [isLoggedIn]);

  // ─── Phase 9 — restore or start chat on open ───────────────────────────────
  useEffect(() => {
    if (!isOpen || initialized) return;
    if (isLoggedIn && userDataLoading) return;

    const saved = loadChatSession();
    if (saved) {
      setMessages(saved.messages);
      setAgentHistory(saved.agentHistory);
    } else {
      const greeting = buildGreeting(isLoggedIn, userData);
      setMessages([{ id: 1, text: greeting, sender: 'bot', timestamp: new Date() }]);
      setAgentHistory([]);
    }
    setInitialized(true);
  }, [isOpen, isLoggedIn, userData, userDataLoading, initialized]);

  // ─── Phase 9 — persist chat (including while popup is closed) ──────────────
  useEffect(() => {
    if (!initialized || messages.length === 0) return;
    saveChatSession({ messages, agentHistory });
  }, [messages, agentHistory, initialized]);

  // ─── Close popup — keep in-memory thread; flush sessionStorage ─────────────
  useEffect(() => {
    if (!isOpen) {
      if (initialized && messages.length > 0) {
        saveChatSession({ messages, agentHistory });
      }
      setShowContactForm(false);
      setInputMessage('');
    }
  }, [isOpen, initialized, messages, agentHistory]);

  // ─── Track auth transitions without wiping chat state ───────────────────────
  useEffect(() => {
    // Keep the current conversation when auth changes.
    // Greeting text is refreshed by the dedicated greeting sync effect.
    wasLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  // Refresh the greeting message when auth state changes mid-chat.
  // This keeps the welcome text in sync without forcing a full reset.
  useEffect(() => {
    if (!initialized || messages.length === 0) return;
    if (isLoggedIn && userDataLoading) return;

    const nextGreeting = buildGreeting(isLoggedIn, userData);
    setMessages((prev) => {
      if (!prev.length) return prev;
      const first = prev[0];
      if (first.sender !== 'bot') return prev;
      if ((first.text || '') === nextGreeting) return prev;
      return [{ ...first, text: nextGreeting }, ...prev.slice(1)];
    });
  }, [initialized, isLoggedIn, userData, userDataLoading]);

  // ─── Auto-scroll ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // ─── URL guard ───────────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => { if (isOpen && shouldHideChatbot()) onClose(); };
    check();
    window.addEventListener('popstate', check);
    return () => window.removeEventListener('popstate', check);
  }, [isOpen, onClose]);

  // Reset close animation when reopening (otherwise popup stays invisible)
  useEffect(() => {
    if (isOpen) setIsClosing(false);
  }, [isOpen]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 300);
  };

  const handleReload = () => {
    clearChatSession();
    setMessages([{ id: 1, text: buildGreeting(isLoggedIn, userData), sender: 'bot', timestamp: new Date() }]);
    setAgentHistory([]);
    setShowContactForm(false);
    setInputMessage('');
    resetForm(setContactForm);
    setContactFormErrors({});
  };

  const handleLoadMore = useCallback(async (messageId) => {
    const msg = messages.find((m) => m.id === messageId);
    if (!msg?.searchContext || loadingMoreMessageId) return;

    const pag = msg.pagination || {};
    const nextOffset = pag.nextOffset != null
      ? pag.nextOffset
      : (pag.offset ?? 0) + (pag.limit ?? 10);
    setLoadingMoreMessageId(messageId);

    try {
      const data = await loadMoreSearchSpaces(msg.searchContext, nextOffset);
      if (!data.found || !data.spaces?.length) {
        setMessages((prev) => prev.map((m) => {
          if (m.id !== messageId) return m;
          return {
            ...m,
            loadMoreNote: data.message || 'No more spaces to show right now.',
            pagination: {
              ...(m.pagination || {}),
              ...(data.pagination || {}),
              hasMore: false,
            },
          };
        }));
        return;
      }

      setMessages((prev) => prev.map((m) => {
        if (m.id !== messageId) return m;
        const seen = new Set((m.spaces || []).map((s) => s.id));
        const merged = [...(m.spaces || [])];
        for (const space of data.spaces) {
          if (space?.id && !seen.has(space.id)) {
            seen.add(space.id);
            merged.push(space);
          }
        }
        const capped = merged.slice(0, MAX_CARDS_PER_SEARCH);
        const mergedPag = { ...(m.pagination || {}), ...(data.pagination || {}) };
        return {
          ...m,
          spaces: capped,
          loadMoreNote: null,
          pagination: {
            ...mergedPag,
            hasMore: Boolean(mergedPag.hasMore) && capped.length < MAX_CARDS_PER_SEARCH,
          },
        };
      }));
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: 'Could not load more spaces right now. Please try again.',
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoadingMoreMessageId(null);
    }
  }, [messages, loadingMoreMessageId]);

  /**
   * dispatchMessage — send a message programmatically (chip removal, retry).
   * Bypasses the input field entirely.
   */
  const dispatchMessage = useCallback(async (text) => {
    if (!text || loading) return;

    if (isOffline) {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), text: 'You appear to be offline. Reconnect and try again.', sender: 'bot', timestamp: new Date() },
      ]);
      return;
    }

    const userMsg    = { id: Date.now(),     text, sender: 'user', timestamp: new Date() };
    const loadingMsg = { id: Date.now() + 1, text: 'Thinking...', sender: 'bot', timestamp: new Date(), isLoading: true };

    setLastUserMessage(text);
    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setLoading(true);

    try {
      const {
        reply, action, spaces, filters, searchMeta, pagination, searchContext, history,
      } = await sendAgentMessage(text, agentHistory, userContext);

      setMessages((prev) => [
        ...prev.filter((m) => !m.isLoading),
        {
          id: Date.now() + 2,
          text: reply,
          sender: 'bot',
          timestamp: new Date(),
          spaces: spaces || null,
          filters: filters || null,
          searchMeta: searchMeta || null,
          pagination: pagination || null,
          searchContext: searchContext || null,
          retryText: text,
        },
      ]);
      setAgentHistory(history);

      if (action?.action === 'show_contact_form') setShowContactForm(true);
      if (action?.type === 'open_booking' && action?.bookingUrl) {
        const bookingPath = toRelativeBookingPath(action.bookingUrl) || action.bookingUrl;
        window.open(bookingPath, '_blank');
      }
    } catch {
      setMessages((prev) => [
        ...prev.filter((m) => !m.isLoading),
        {
          id: Date.now() + 2,
          text: 'I\'m having trouble connecting right now.',
          sender: 'bot',
          timestamp: new Date(),
          isError: true,
          retryText: text,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [loading, agentHistory, userContext, isOffline]);

  const handleSend = useCallback(async () => {
    const text = inputMessage.trim();
    if (!text || loading) return;
    setInputMessage('');
    await dispatchMessage(text);
  }, [inputMessage, loading, dispatchMessage]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  const handleContactFormChange = (e) => {
    handleFormFieldChange(e, setContactForm, contactForm);
    const { name } = e.target;
    if (contactFormErrors[name]) {
      setContactFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const validation = validateContactForm(contactForm);
      if (!validation.isValid) {
        setContactFormErrors(validation.errors);
        return;
      }
      const sanitized = sanitizeFormData(contactForm);
      const formatted = formatFormDataForSubmission(sanitized);
      await dispatch(submitContactForm(formatted));
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), text: "Thank you! Your message has been sent. We'll get back to you soon.", sender: 'bot', timestamp: new Date() },
      ]);
      setShowContactForm(false);
      resetForm(setContactForm);
      setContactFormErrors({});
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), text: 'Sorry, there was an error sending your message. Please try again.', sender: 'bot', timestamp: new Date() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ts) => {
    try { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (!isOpen || shouldHideChatbot()) return null;

  const isReady = !isLoggedIn || (isLoggedIn && userData && !userDataLoading);

  return (
    <>
      {/* Loading state while user data fetches */}
      {isOpen && !isReady && (
        <div className="chatbot-overlay">
          <div className="chatbot-popup">
            <div className="chatbot-loading-container">
              <div className="chatbot-loading-content">
                <div className="chatbot-avatar">
                  <img src={require('../../../assets/images/agent/chatbot-avatar.png')} alt="Chatbot Avatar" width="48" height="48" />
                </div>
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <span>Loading your personalized experience...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main chat popup */}
      {isReady && (
        <div className={`chatbot-overlay ${isClosing ? 'closing' : ''}`}>
          <div className={`chatbot-popup ${isClosing ? 'closing' : ''}`}>

            {/* Header */}
            <div className="chatbot-header">
              <div className="chatbot-title">
                <div className="chatbot-avatar">
                  <img src={require('../../../assets/images/agent/chatbot-avatar.png')} alt="Chatbot Avatar" width="32" height="32" />
                </div>
                <div className="chatbot-info">
                  <h3>{CHATBOT_CONFIG.NAME}</h3>
                </div>
              </div>
              <div className="header-actions">
                <button
                  type="button"
                  className="reset-chat-button"
                  onClick={handleReload}
                  aria-label="Reset chat and start a new conversation"
                  title="Clear this chat and start fresh"
                >
                  <svg className="reset-chat-button__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M4 12a8 8 0 0 1 13.66-5.66M20 7V3h-4M20 12a8 8 0 0 1-13.66 5.66M4 17v4h4"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="reset-chat-button__label">Reset chat</span>
                </button>
                <button type="button" className="close-button" onClick={handleClose} aria-label="Close chat">
                  <img src={require('../../../assets/images/agent/close-circle.svg')} alt="" width="22" height="22" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="chatbot-messages">
              {messages.map((msg, index) => (
                <div key={msg.id} className={`message ${msg.sender}`}>
                  <div className={index === 0 ? 'initial-message-content' : 'message-content'}>
                    {msg.isLoading ? (
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                        <span>{msg.text}</span>
                      </div>
                    ) : (
                      <>
                        <div className="initial-message-row">
                          <div className="message-text">
                            {index === 0 ? (
                              // First message: bold first line
                              (() => {
                                const displayText = replyTextForMessage(msg);
                                const parts = displayText.split('\n\n');
                                return (
                                  <>
                                    <strong>{parts[0]}</strong>
                                    {parts.length > 1 && <><br />{renderText(parts.slice(1).join('\n\n'))}</>}
                                  </>
                                );
                              })()
                            ) : (
                              renderText(replyTextForMessage(msg))
                            )}
                          </div>
                          {index === 0 && (
                            <div className="initial-message-avatar">
                              <img
                                src={require('../../../assets/images/agent/chatbot-avatar-reflect.png')}
                                width="100" height="100"
                                alt="Spacey"
                              />
                            </div>
                          )}
                        </div>

                        {/* Phase 8 — error state with retry */}
                        {msg.isError && (
                          <div className="v2-error-state">
                            <span className="v2-error-icon">⚠️</span>
                            <span className="v2-error-text">
                              Having trouble connecting. Check your connection or{' '}
                              <button className="v2-retry-btn" onClick={() => dispatchMessage(msg.retryText)}>
                                try again
                              </button>.
                            </span>
                            <button
                              className="v2-contact-btn"
                              onClick={() => dispatchMessage('connect me to an agent')}
                            >
                              Talk to a human →
                            </button>
                          </div>
                        )}

                        {/* Phase 8 — search empty / API error (no cards) */}
                        {msg.searchMeta && (
                          <div
                            className={`v2-search-meta v2-search-meta--${msg.searchMeta.type}`}
                          >
                            <span className="v2-search-meta-icon">
                              {msg.searchMeta.type === 'error' ? '⚠️'
                                : (msg.searchMeta.type === 'time_invalid' || msg.searchMeta.type === 'date_invalid')
                                  ? '🕐'
                                  : '🔍'}
                            </span>
                            <p className="v2-search-meta-text">
                              {msg.searchMeta.message
                                || (msg.searchMeta.type === 'error'
                                  ? 'Spaces are slow to load right now.'
                                  : msg.searchMeta.type === 'time_invalid'
                                    ? "I couldn't read that time. Try something like 10:00 am – 11:00 am."
                                    : msg.searchMeta.type === 'date_invalid'
                                      ? "That date isn't in the availability window."
                                      : 'No spaces matched those filters.')}
                            </p>
                            <div className="v2-search-meta-actions">
                              {(msg.searchMeta.type === 'time_invalid' || msg.searchMeta.type === 'date_invalid')
                                && msg.retryText && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn v2-empty-action-primary"
                                  onClick={() => dispatchMessage(msg.retryText)}
                                >
                                  Try again
                                </button>
                              )}
                              {msg.searchMeta.type === 'error' && msg.retryText && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn v2-empty-action-primary"
                                  onClick={() => dispatchMessage(msg.retryText)}
                                >
                                  Try again
                                </button>
                              )}
                              {msg.searchMeta.type === 'empty' && msg.filters?.location
                                && !/colombo/i.test(msg.filters.location) && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn"
                                  onClick={() => dispatchMessage(
                                    `show ${msg.filters.spaceType || 'spaces'} in Colombo`
                                  )}
                                >
                                  Try Colombo
                                </button>
                              )}
                              {msg.searchMeta.type === 'empty' && msg.filters?.date && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn"
                                  onClick={() => dispatchMessage(
                                    buildChipRemovalMessage('date', msg.filters)
                                  )}
                                >
                                  Search without date
                                </button>
                              )}
                              {msg.searchMeta.type === 'empty' && msg.filters?.maxPrice && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn"
                                  onClick={() => dispatchMessage('search without a budget limit')}
                                >
                                  Remove budget cap
                                </button>
                              )}
                              {msg.searchMeta.type === 'empty'
                                && Array.isArray(msg.filters?.amenities)
                                && msg.filters.amenities.length > 0 && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn"
                                  onClick={() => dispatchMessage(
                                    buildChipRemovalMessage('amenities', msg.filters),
                                  )}
                                >
                                  Remove amenity filters
                                </button>
                              )}
                              {msg.searchMeta.type === 'error' && (
                                <button
                                  type="button"
                                  className="v2-empty-action-btn"
                                  onClick={() => dispatchMessage('connect me to an agent')}
                                >
                                  Talk to a human →
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Phase 2 — filter chips (also when search returned no matches) */}
                        {msg.filters && ((msg.spaces && msg.spaces.length > 0) || msg.searchMeta?.type === 'empty') && msg.searchMeta?.type !== 'time_invalid' && msg.searchMeta?.type !== 'date_invalid' && (
                          <div className="v2-filter-chips">
                            {msg.filters.spaceType && (
                              <span className="v2-chip">
                                {msg.filters.spaceType}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label={`Remove ${msg.filters.spaceType} filter`}
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('spaceType', msg.filters))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {msg.filters.location && (
                              <span className="v2-chip">
                                📍 {msg.filters.location}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label={`Remove ${msg.filters.location} filter`}
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('location', msg.filters))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {msg.filters.date && (
                              <span className="v2-chip">
                                📅 {msg.filters.date}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label="Remove date filter"
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('date', msg.filters))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {msg.filters.timeRange && (
                              <span className="v2-chip">
                                🕐 {msg.filters.timeRange}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label="Remove time filter"
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('time', msg.filters))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {msg.filters.peopleCount && (
                              <span className="v2-chip">
                                👥 {msg.filters.peopleCount}+ people
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label="Remove capacity filter"
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('peopleCount', msg.filters))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {msg.filters.maxPrice && (
                              <span className="v2-chip">
                                💰 ≤ LKR {msg.filters.maxPrice.toLocaleString()}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label="Remove budget filter"
                                  onClick={() => dispatchMessage(buildChipRemovalMessage('maxPrice', msg.filters))}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            )}
                            {Array.isArray(msg.filters.amenities) && msg.filters.amenities.map((amenity, amenityIdx) => (
                              <span key={`amenity-${amenityIdx}-${amenity}`} className="v2-chip v2-chip-amenity">
                                {amenity}
                                <button
                                  type="button"
                                  className="v2-chip-remove"
                                  aria-label={`Remove ${amenity} filter`}
                                  onClick={() => dispatchMessage(
                                    buildChipRemovalMessage('amenity', msg.filters, { amenityName: amenity }),
                                  )}
                                >
                                  <span className="v2-chip-x">✕</span>
                                </button>
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Phase 3 — rich space cards */}
                        {msg.spaces && msg.spaces.length > 0 && (() => {
                          const isAvailability = msg.spaces[0]?.bookingMode === 'availability';
                          const headerText = isAvailability ? 'Available Spaces' : 'Spaces You May Like';
                          const pag = msg.pagination;
                          const shownCount = msg.spaces.length;
                          const totalMatching = pag?.totalMatching ?? shownCount;
                          const remainingCount = pag?.remaining != null
                            ? pag.remaining
                            : Math.max(0, totalMatching - shownCount);
                          const canLoadMore = Boolean(msg.searchContext)
                            && shownCount < MAX_CARDS_PER_SEARCH
                            && Boolean(pag?.hasMore);
                          const resultsMapUrl = msg.filters
                            ? buildAgentSearchResultsUrl(msg.filters, msg.searchContext, { openMap: true })
                            : null;
                          return (
                          <div className="v2-space-cards">
                            <div className="v2-spaces-header">
                              <div className="v2-spaces-title">{headerText}</div>
                              {resultsMapUrl && (
                                <a
                                  href={resultsMapUrl}
                                  className="v2-view-map-link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View on map →
                                </a>
                              )}
                            </div>
                            {totalMatching > shownCount && (
                              <p className="v2-results-summary">
                                Showing {shownCount} of {totalMatching} spaces
                              </p>
                            )}
                            {msg.spaces.map((space, si) => {
                              const imgUrl = buildImageUrl(resolveSpaceImagePublicId(space));
                              const cardBookingUrl = resolveSpaceCardBookingUrl(space, () => buildBookingUrl(
                                space.id,
                                space.country,
                                space.bookingUnit || 'hour',
                                space.bookingStartDate,
                                space.bookingEndDate,
                                space.eventTypeId,
                                space.bookingStartTime,
                                space.bookingEndTime,
                                msg.filters?.peopleCount,
                              ));
                              const pricingDisplay = space.matchedPrice
                                ? `${space.matchedPrice.currency} ${space.matchedPrice.amount.toLocaleString()} / ${space.matchedPrice.perUnit}`
                                : (space.pricing?.find((p) => p !== 'Contact for pricing') || null);
                              const locationText = [space.address, space.city].filter(Boolean).join(', ');
                              const buttonLabel = isAvailability ? 'Book Now →' : 'View Details →';
                              const displayName = space.organizationName || space.hostedBy || null;

                              // Phase 1 — available slots (only when API returned availability)
                              const avail = isAvailability ? space.availability : null;
                              const availableSlots = avail?.slots?.filter((s) => s.status) || [];

                              return (
                                <div key={space.id || `space-${si}`} className="v2-space-card">
                                  <div className="v2-space-card-image">
                                    {imgUrl ? (
                                      <img
                                        src={imgUrl}
                                        alt={space.name}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div className="v2-space-placeholder" style={{ display: imgUrl ? 'none' : 'flex' }}>
                                      🏢
                                    </div>
                                  </div>
                                  <div className="v2-space-card-body">
                                    <div className="v2-space-name">{space.name}</div>
                                    {displayName && (
                                      <div className="v2-space-hosted">by {displayName}</div>
                                    )}
                                    {locationText && (
                                      <div className="v2-space-location">
                                        <span className="v2-location-icon">📍</span>
                                        <span>
                                          {locationText}
                                          {space.distanceKm != null && (
                                            <span className="v2-distance"> · {space.distanceKm.toFixed(1)} km</span>
                                          )}
                                        </span>
                                      </div>
                                    )}
                                    {pricingDisplay && (
                                      <div className="v2-space-pricing">{pricingDisplay}</div>
                                    )}
                                    {space.capacity && (
                                      <div className="v2-space-capacity">Up to {space.capacity} people</div>
                                    )}

                                    {/* Phase 3 — amenity tags */}
                                    {space.amenities && space.amenities.length > 0 && (
                                      <div className="v2-amenity-tags">
                                        {space.amenities.slice(0, 4).map((a, ai) => (
                                          <span key={ai} className="v2-amenity-tag">{a.name}</span>
                                        ))}
                                        {space.amenities.length > 4 && (
                                          <span className="v2-amenity-tag v2-amenity-more">
                                            +{space.amenities.length - 4}
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Phase 1 — inline availability */}
                                    {avail && (
                                      <div className="v2-availability">
                                        {space.requestedTimeAvailable === false && space.timeNote && (
                                          <div className="v2-avail-label v2-avail-warning">⚠ {space.timeNote}</div>
                                        )}
                                        {space.requestedTimeAvailable === true && msg.filters?.timeRange && (
                                          <div className="v2-avail-label">✓ Available for {msg.filters.timeRange}</div>
                                        )}
                                        {availableSlots.length > 0 ? (
                                          <>
                                            <div className="v2-avail-label">✓ Available slots</div>
                                            {availableSlots.slice(0, 3).map((slot, slotI) => (
                                              <div key={slotI} className="v2-slot">
                                                {slot.startTime} – {slot.endTime}
                                              </div>
                                            ))}
                                            {availableSlots.length > 3 && (
                                              <div className="v2-slot-more">
                                                +{availableSlots.length - 3} more slots
                                              </div>
                                            )}
                                          </>
                                        ) : avail.availableSeats !== null && avail.availableSeats !== undefined ? (
                                          <div className="v2-avail-label">
                                            ✓ {avail.availableSeats} seat{avail.availableSeats !== 1 ? 's' : ''} available
                                          </div>
                                        ) : space.requestedTimeAvailable !== false ? (
                                          <div className="v2-avail-label">✓ Available</div>
                                        ) : null}
                                      </div>
                                    )}

                                    <button className="v2-book-btn" onClick={() => window.open(cardBookingUrl, '_blank')}>
                                      {buttonLabel}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                            {msg.loadMoreNote && (
                              <p className="v2-pagination-note">{msg.loadMoreNote}</p>
                            )}
                            {canLoadMore && (
                              <div className="v2-pagination">
                                <p className="v2-pagination-text">
                                  {remainingCount > 0
                                    ? `+${remainingCount} more - narrow down with budget or date, or`
                                    : 'Load more results, or'}
                                </p>
                                <button
                                  type="button"
                                  className="v2-load-more-btn"
                                  disabled={loadingMoreMessageId === msg.id}
                                  onClick={() => handleLoadMore(msg.id)}
                                >
                                  {loadingMoreMessageId === msg.id ? 'Loading...' : 'Show 10 more'}
                                </button>
                              </div>
                            )}
                            {!canLoadMore && pag && pag.totalMatching > shownCount && shownCount >= MAX_CARDS_PER_SEARCH && (
                              <p className="v2-pagination-cap">
                                Showing {MAX_CARDS_PER_SEARCH} of {pag.totalMatching} matches. Refine filters for a shorter list.
                              </p>
                            )}
                          </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                  <span className="message-time">{formatTime(msg.timestamp)}</span>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Contact form */}
            {showContactForm && (
              <div className="chatbot-contact-form">
                <h4>{CONTACT_FORM_MESSAGES.TITLE}</h4>
                <div className="agent-contact-details">
                  <div className="agent-contact-message">{CONTACT_FORM_MESSAGES.AGENT_MESSAGE}</div>
                  <div className="agent-phone-number">
                    <a href={`tel:${CONTACT_FORM_MESSAGES.AGENT_PHONE}`} className="phone-link">
                      {CONTACT_FORM_MESSAGES.AGENT_PHONE}
                    </a>
                  </div>
                </div>
                <form onSubmit={handleContactFormSubmit}>
                  {[
                    { label: FORM_LABELS.NAME, type: INPUT_TYPES.TEXT, name: FORM_FIELD_NAMES.NAME, placeholder: FORM_PLACEHOLDERS.NAME, value: contactForm.name },
                    { label: FORM_LABELS.EMAIL, type: INPUT_TYPES.EMAIL, name: FORM_FIELD_NAMES.EMAIL, placeholder: FORM_PLACEHOLDERS.EMAIL, value: contactForm.email },
                    { label: FORM_LABELS.PHONE_NUMBER, type: INPUT_TYPES.TEL, name: FORM_FIELD_NAMES.PHONE_NUMBER, placeholder: FORM_PLACEHOLDERS.PHONE_NUMBER, value: contactForm.phoneNumber, optional: true },
                  ].map((field) => (
                    <div key={field.name} className="form-group">
                      <label className="form-label">
                        {field.label} {field.optional && <span className="optional-text">(Optional)</span>}
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        placeholder={field.placeholder}
                        value={field.value}
                        onChange={handleContactFormChange}
                        className={contactFormErrors[field.name] ? 'error' : ''}
                      />
                      {contactFormErrors[field.name] && <div className="form-error">{contactFormErrors[field.name]}</div>}
                    </div>
                  ))}
                  <div className="form-group">
                    <label className="form-label">{FORM_LABELS.MESSAGE}</label>
                    <textarea
                      name={FORM_FIELD_NAMES.MESSAGE}
                      placeholder={FORM_PLACEHOLDERS.MESSAGE}
                      value={contactForm.message}
                      onChange={handleContactFormChange}
                      className={contactFormErrors.message ? 'error' : ''}
                    />
                    {contactFormErrors.message && <div className="form-error">{contactFormErrors.message}</div>}
                  </div>
                  <div className="form-actions">
                    <button type="submit" disabled={loading || !isFormReadyToSubmit(contactForm)}>
                      {loading ? LOADING_MESSAGES.SENDING_MESSAGE : FORM_BUTTONS.SEND_MESSAGE}
                    </button>
                    <button type="button" onClick={() => { setShowContactForm(false); resetForm(setContactForm); setContactFormErrors({}); }}>
                      {FORM_BUTTONS.CANCEL}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Phase 8 — offline banner */}
            {isOffline && (
              <div className="v2-offline-banner">
                You're offline — reconnect to keep chatting.
              </div>
            )}

            {/* Input */}
            {!showContactForm && (
              <div className="chatbot-input">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything about workspaces..."
                  disabled={loading}
                />
                <button onClick={handleSend} disabled={loading || !inputMessage.trim()}>
                  {loading ? '⏳' : (
                    <div className="input-send">
                      <img src={require('../../../assets/images/agent/send.svg')} alt="Send" width="23" height="23" />
                    </div>
                  )}
                </button>
              </div>
            )}

            {/* Disclaimer + info */}
            <div className="chatbot-disclaimer">
              <div className="disclaimer-content">
                <span className="disclaimer-text">{DISCLAIMER_MESSAGES.TEXT}</span>
                <button className="info-button" onClick={() => setShowInfo(!showInfo)} title="More information">
                  <img src={require('../../../assets/images/agent/info-circle.svg')} alt="Info" width="16" height="16" />
                </button>
              </div>

              {showInfo && (
                <div className="info-modal">
                  <div className="info-content">
                    <div className="info-header">
                      <h4>{INFO_PANEL_MESSAGES.ABOUT_SPACEY}</h4>
                      <button className="close-icon" onClick={() => setShowInfo(false)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor" /></svg>
                      </button>
                    </div>
                    <div className="info-body">
                      <p>{INFO_PANEL_MESSAGES.DESCRIPTION}</p>
                      <h5>{INFO_PANEL_MESSAGES.HELP_WITH_TITLE}</h5>
                      <ul>
                        {HELP_INFORMATION.CAN_HELP_WITH.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                      <h5>{INFO_PANEL_MESSAGES.CONTACT_AGENT_TITLE}</h5>
                      <ul>
                        {HELP_INFORMATION.CONTACT_AGENT.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                      <h5>{INFO_PANEL_MESSAGES.IMPORTANT_NOTES_TITLE}</h5>
                      <ul>
                        {HELP_INFORMATION.IMPORTANT_NOTES.map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                      <button className="close-info" onClick={() => setShowInfo(false)}>{INFO_PANEL_MESSAGES.GOT_IT}</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  );
};

ChatbotPopupV2.displayName = 'ChatbotPopupV2';

export default ChatbotPopupV2;

// ─── Greeting builder ─────────────────────────────────────────────────────────
function buildGreeting(isLoggedIn, userData) {
  if (!isLoggedIn) {
    return "Hi there,\n\nI am Spacey, your smart assistant ready to help you find the perfect space.\n\nJust tell me what you're looking for - workspace type, location, or budget - and I'll find the best options for you.";
  }
  const name = userData?.userName?.split(' ')[0] || 'there';
  const count = userData?.bookingCount || 0;
  if (count > 0) {
    return `Hi ${name},\n\nYou've booked ${count} time${count > 1 ? 's' : ''} with MillionSpaces! Great to have you back.\n\nWhat are you looking to book today?`;
  }
  return `Hi ${name},\n\nWelcome to MillionSpaces! I'm Spacey, your workspace assistant.\n\nTell me what kind of space you need and I'll find the best options for you.`;
}
